import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import {
  DrainIssueType,
  useDrainsStore,
} from '../../../../hooks/useDrainsStore';
import { MultipleOptionsTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../../../../constants/errors';
import { ZodError } from 'zod';
import {
  BLOCKED_DRAIN,
  BROKEN_DRAIN_COVER,
  BURST_WATER_MAIN,
  FLOODING,
  MISSING_DRAIN_COVER,
  SMELLY_DRAIN,
  SOMETHING_ELSE,
} from '../../../../constants/drains';

export const SelectDrainsIssue: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    update,
    resetSelective,
    data: { issueType },
  } = useDrainsStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Is the issue related to any one of these?';

  const options = [
    {
      id: BLOCKED_DRAIN,
      title: BLOCKED_DRAIN,
      labelText: 'Blocked drain',
      isDefault: issueType === BLOCKED_DRAIN,
      isLabelBold: false,
    },
    {
      id: BROKEN_DRAIN_COVER,
      title: BROKEN_DRAIN_COVER,
      labelText: 'Broken drain cover',
      isDefault: issueType === BROKEN_DRAIN_COVER,
      isLabelBold: false,
    },
    {
      id: BURST_WATER_MAIN,
      title: BURST_WATER_MAIN,
      labelText: 'Burst water main',
      isDefault: issueType === BURST_WATER_MAIN,
      isLabelBold: false,
    },
    {
      id: FLOODING,
      title: FLOODING,
      labelText: 'Flooding',
      isDefault: issueType === FLOODING,
      isLabelBold: false,
    },
    {
      id: MISSING_DRAIN_COVER,
      title: MISSING_DRAIN_COVER,
      labelText: 'Missing drain cover',
      isDefault: issueType === MISSING_DRAIN_COVER,
      isLabelBold: false,
    },
    {
      id: SMELLY_DRAIN,
      title: SMELLY_DRAIN,
      labelText: 'Smelly drain',
      isDefault: issueType === SMELLY_DRAIN,
      isLabelBold: false,
    },
    {
      id: SOMETHING_ELSE,
      title: SOMETHING_ELSE,
      labelText: `It's something else`,
      isDefault: issueType === SOMETHING_ELSE,
      isLabelBold: false,
    },
  ];

  const handleChange = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as DrainIssueType;

    if (updatedSelection) {
      update('issueType', updatedSelection);
      update('tellUsMoreDetails', '');
    } else {
      resetSelective();
    }
  };

  const handleNavigate = () => {
    switch (issueType) {
      case BLOCKED_DRAIN:
        navigate(RoutesList.PRIVATE_PROPERTY_ISSUE);
        break;
      case BROKEN_DRAIN_COVER:
        navigate(RoutesList.CALL_US); //P1
        break;
      case BURST_WATER_MAIN:
        navigate(RoutesList.THAMES_WATER); //Thames water
        break;
      case FLOODING:
        navigate(RoutesList.PRIVATE_PROPERTY_ISSUE);
        break;
      case MISSING_DRAIN_COVER:
        navigate(RoutesList.CALL_US); //P1
        break;
      case SMELLY_DRAIN:
        navigate(RoutesList.PRIVATE_PROPERTY_ISSUE);
        break;
      case SOMETHING_ELSE:
        navigate(RoutesList.PRIVATE_PROPERTY_ISSUE);
        break;
      default:
        return false;
    }
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.DRAINS_HOME);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'issueType',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          issueType,
        });
        handleNavigate();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            message: SELECT_OPTIONS_ERROR.required.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <MultipleOptionsTemplate
      title={title}
      radioOptions={[
        {
          onChange: handleChange,
          options: options,
          title: '',
        },
      ]}
      errorPath="issueType"
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      onChange={handleChange}
    />
  );
};
